import { useEffect, useState, memo, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip
} from '@mui/material';
import { Search, Info } from '@mui/icons-material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import VivproDatagrid from '../Datagrid';
import styles from '../RegulatoryInformation/styles/ApplicationTableView.styles';
import { getAllActiveIngredients } from '../../api/ActiveIngredientCuration';
import { sourceList, qcStatusList } from './constants';

interface ActiveIngredientData {
  id: number;
  active_ingredient: string;
  active_ingredient_qc_status: string;
  base_trials_count: number;
  product_qc_status: string;
  products: {
    application_number: string;
    overlay_trials_count: number;
    id: number;
    qc_status: string;
  }[];
  source: string;
}

const ActiveIngredientCurationList = () => {
  const history = useHistory();
  const [activeIngredients, setActiveIngredients] = useState<ActiveIngredientData[]>([]);
  const [selectedSource, setSelectedSource] = useState<string>('ALL');
  const [selectedQcStatus, setSelectedQcStatus] = useState<string>('ToQc');
  const [searchValue, setSearchValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchActiveIngredients = async () => {
      try {
        setIsLoading(true);
        const response = await getAllActiveIngredients();

        if (response?.status !== 200 || !response?.data) {
          return;
        }

        setActiveIngredients(response?.data || []);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching active ingredients', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActiveIngredients();
  }, []);

  const filteredActiveIngredients = useMemo(() => {
    const filtered = activeIngredients.filter(ingredient => {
      return (
        ingredient.active_ingredient.toLowerCase().includes(searchValue.toLowerCase()) &&
        (selectedSource === 'ALL' ||
          ingredient.source?.toLowerCase() === selectedSource.toLowerCase()) &&
        (selectedQcStatus === 'ALL' ||
          ingredient.active_ingredient_qc_status?.toLowerCase() ===
            selectedQcStatus.toLowerCase() ||
          ingredient.product_qc_status?.toLowerCase() === selectedQcStatus.toLowerCase())
      );
    });
    return filtered;
  }, [activeIngredients, searchValue, selectedSource, selectedQcStatus]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 65, flex: 0.2 },
    { field: 'active_ingredient', headerName: 'Active Substance', width: 200, flex: 1 },
    {
      field: 'source',
      headerName: 'Source',
      width: 150,
      flex: 0.2,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams<any>) => {
        return params?.row?.source?.toUpperCase();
      }
    },
    {
      field: 'base_trials_count',
      headerName: 'Base Trials',
      width: 200,
      flex: 0.2,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'active_ingredient_qc_status',
      headerName: 'Status',
      width: 75,
      flex: 0.2,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'Products',
      width: 200,
      flex: 0.2,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams<any>) => {
        return params?.row?.products?.length;
      }
    },
    {
      field: 'Overlay Trials',
      width: 200,
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams<any>) => {
        let total = 0;

        params?.row?.products?.forEach((product: any) => {
          total += product.overlay_trials_count;
        });

        return (
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography>{total}</Typography>
            <Tooltip
              title={
                <span style={{ whiteSpace: 'pre-line' }}>
                  {params?.row?.products
                    .map((item: any) => `${item.application_number}: ${item.overlay_trials_count}`)
                    .join('\n')}
                </span>
              }>
              <Info
                sx={{
                  fontSize: 18
                }}
              />
            </Tooltip>
          </Stack>
        );
      }
    },
    {
      field: 'product_qc_status',
      headerName: 'Status',
      width: 100,
      flex: 0.2,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'Curate',
      width: 75,
      flex: 0.2,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }: any) => {
        return (
          <Button
            variant='contained'
            color='primary'
            onClick={() => {
              history.push(`/active_ingredient_curation/${row.id}`);
            }}>
            <Typography>Curate</Typography>
          </Button>
        );
      }
    }
  ];

  return (
    <Box height='10vh'>
      <Box sx={{ height: '50vh', width: '95%', paddingTop: '10vh', paddingLeft: '10vh' }}>
        <Box>
          <Typography sx={styles.Typography}>Active Ingredient Curation</Typography>
        </Box>
        <Box sx={styles.root} pr={2}>
          <Stack direction='column' alignItems='flex-start' spacing={2}>
            <Stack direction='row' spacing={2} pt={3} width='100%'>
              <TextField
                variant='outlined'
                type='search'
                id='search_active_ingredient'
                label='Search Active Ingredient'
                value={searchValue}
                InputProps={{
                  style: { fontSize: '1.5vh', minWidth: '250px' },
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search />
                    </InputAdornment>
                  )
                }}
                InputLabelProps={{
                  style: { fontSize: '1.5vh' }
                }}
                onChange={e => {
                  setSearchValue(e.target.value);
                }}
              />
              <FormControl>
                <InputLabel id='source'>Source</InputLabel>
                <Select
                  labelId='source'
                  name='source'
                  id='source'
                  value={selectedSource}
                  label='Source'
                  sx={styles.select}
                  onChange={(e: any) => {
                    setSelectedSource(e.target.value);
                  }}>
                  {sourceList?.map(option => {
                    return (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel id='qc_status'>QC Status</InputLabel>
                <Select
                  labelId='qc_status'
                  name='qc_status'
                  id='qc_status'
                  value={selectedQcStatus}
                  label='QC Status'
                  sx={styles.select}
                  onChange={(e: any) => {
                    setSelectedQcStatus(e.target.value);
                  }}>
                  {qcStatusList.map(option => {
                    return (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Stack>
            <Box height='70vh' width='100%'>
              <VivproDatagrid
                rows={filteredActiveIngredients}
                columnsMapping={columns}
                rowId='id'
                loading={isLoading}
                autoRowHeight
              />
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(ActiveIngredientCurationList);
